define('modularis/web/widgets/entityDropDownList',
   ['jquery', 'kendo', 'clientCache', 'logger', 'util'],
   function ($, kendo, clientCache, logger, util) {
       'use strict';
       var ui = kendo.ui, Widget = ui.Widget;

       var changeEventName = 'change',
          dataBoundEventName = 'dataBound';

       //#region Widget definition

       /**
        * @see {@link https://docs.telerik.com/kendo-ui/controls/editors/dropdownlist/overview}
        * @classdesc Modularis EntityDropDownList widget. Inherits from {@link https://docs.telerik.com/kendo-ui/api/javascript/ui/dropdownlist|kendo.ui.Dropdownlist}
        * <p>This class allows creating a drop-down list of an entity object.
        * For use this feature, must heritage of this class and overwrite the loadData and onChange method.</p>
        * @constructs modularis.web.widgets.EntityDropDownList
        * @param {Object} options - Widget configuration options. Check the options property to see the available settings.
        * @property {Object} options - Widget options.
        * @property {string} options.name - Widget name, how it is going to be invoked.
        * @property {string} [options.optionLabel = 'Select entity'] - Define the text of the default empty item.
        * @property {string} [options.dropDownTextField = 'PropertyName'] - Entity property name which its value will be the text content of the list items.
        * @property {string} [options.dropDownValueField = 'PropertyValue'] - Entity property name which its value will be the value of the data item.
        * @property {?string} [options.value = null] - Default value selected.
        * @property {boolean} [options.enable = true] - If set to false the widget will be disabled and will not allow user input. By default its value is true.
        * @property {boolean} [options.valuePrimitive = false] - If set to true, specify that the View-Model field should be updated with the selected item value. By default its value is false.
        * @property {string} [options.attrId] - The attribute unique identification of the widget, if it does not have value by default a GUID is assigned.
        * @property {string} [options.attrName] - The attribute name of the widget, if it does not have value by default a GUID is assigned.
        * @property {boolean} [options.attrRequired = false] - If set to true indicates that attribute is required and has to select a value. By default its value is false.
        * @property {string} [options.attrRequiredMsg = 'Select entity'] - Validation error message.
        * */
       var EntityDropDownList = Widget.extend({

           _dropDownList: null,
           _value: null,
           _old: null,
           _items: [],
           _dataSource: null,
           init: function (element, options) {
               var that = this;
               Widget.fn.init.call(that, element, options);
               /*eslint-disable no-param-reassign*/
               if (!options.attrId) { options.attrId = util.newGuid(); }
               if (!options.attrName) { options.attrName = options.attrId; }

               that.createSelector();

               if (options.value) {
                   that.value(options.value);
               }
               /*eslint-enable no-param-reassign*/
           },

           options: {
               name: 'EntityDropDownList',
               optionLabel: 'Select entity',
               dropDownTextField: 'PropertyName',
               dropDownValueField: 'PropertyValue',
               value: null,
               enable: true,
               valuePrimitive: false,

               attrId: null,
               attrName: null,
               attrRequired: false,
               attrRequiredMsg: 'Select entity'
           },

           events: [changeEventName, dataBoundEventName],

           /**
             * @name loadData
             * @function modularis.web.widgets.EntityDropDownList#loadData
             * @description This function must be overwritten to load the data for the widget.
             * @memberof modularis.web.widgets.EntityDropDownList
             * @param {object} callback - Function to be called after the data is loaded.
           */
           loadData: function (/*callback*/) {

           },
           /**
             * @name onChange
             * @function modularis.web.widgets.EntityDropDownList#onChange
             * @description This function can be overwritten to execute additional code from the widget after the value has been changed.
             * @memberof modularis.web.widgets.EntityDropDownList
           */
           onChange: function () {
                
           },
           createSelector: function () {
               var that = this;

               if (!clientCache.isAuthenticated()) { return; }

               that.loadData(function (data, error) {

                   if (!util.success(data, error)) {
                       logger.error('Error loading data');
                       return;
                   }


                   if (data instanceof Array) {
                       that._items = data;
                   } else if (util.hasItems(data)) {
                       that._items = data.Items;
                   }

                   $(that.element).attr('id', that.options.attrId);
                   $(that.element).attr('name', that.options.attrName);

                   if (that.options.attrRequired) {
                       $(that.element).prop('required', true);
                       $(that.element).data('required-msg', that.options.attrRequiredMsg);
                   }
                   that._dataSource = new kendo.data.DataSource({
                       data: that._items,
                       schema: {
                           model: {
                               id: that.options.dropDownValueField
                           }
                       }
                   });

                   $(that.element).kendoDropDownList({
                       dataSource: that._dataSource,
                       dataTextField: that.options.dropDownTextField,
                       dataValueField: that.options.dropDownValueField,
                       optionLabel: that.options.optionLabel,
                       value: that._value,
                       enable: that.options.enable,
                       valuePrimitive: that.options.valuePrimitive,
                       //Events
                       change: $.proxy(that._handleChange, that),
                       dataBound: $.proxy(that._handleDataBound, that)
                   });
                   that._dropDownList = $(that.element).getKendoDropDownList();

               });
           },

           //Required for the value binding.
           value: function (value) {
               var that = this;
               if (!util.isDefined(value)) {
                   return that._value;
               }
               that._update(value);
               that._old = that._value;
               return undefined;
           },

           //Required for the enabled binding
           enable: function (enableValue) {
               var that = this;
               if (that._dropDownList) {
                   that._dropDownList.enable(enableValue);
               }
           },

           //Event handlers

           _handleChange: function () {
               var that = this;
               var selectedValue = that._dropDownList.value();
               that._changeValue(selectedValue);
           },

           _handleDataBound: function (event) {
               var that = this;
               if (!that.value() && !that.options.optionLabel) {
                   that._selectFirst();
               }
               this.trigger(dataBoundEventName, event);
           },

           _update: function (value) {
              var that = this;
              that._value = value;
              if (that._dropDownList) {
                 if (that.options.valuePrimitive || !that._value) {
                    that._dropDownList.value(that._value);
                 } else {
                    that._dropDownList.value(that._value[that.options.dropDownValueField]);
                 }
              }
           },
           _selectFirst: function () {
               var that = this;
               var dataSource = that._dropDownList ? that._dropDownList.dataSource : that._dataSource;
               if (dataSource.data().length === 0) { return; }
               var first = dataSource.at(0);
               if (that.options.valuePrimitive) {
                   that._changeValue(first[that.options.dropDownValueField]);
               } else {
                   that._changeValue(first);
               }
           },
           _changeValue: function (selectedValue) {
               var that = this;
               if (that.options.valuePrimitive) {
                   that._change(selectedValue);
               } else {
                   var dataSource = that._dropDownList ? that._dropDownList.dataSource : that._dataSource;
                   var selectedItem = dataSource.get(selectedValue);
                   that._change(selectedItem);
               }
           },
           _change: function (value) {
               var that = this;
               if (that._old !== value) {
                   that._update(value);
                   that._old = value;
                   that.onChange();
                   that.trigger(changeEventName);
               }
           }
       });

       //#endregion

       return {
           widgetClass: EntityDropDownList
       };

   }
);
